import React from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import SPADataTable from '../../shared/SPADataTable';
import type { AssignmentRow } from '../useAssignmentRows';
import type { MatchRow } from '../useCourseMatch';
import RepairCell from '../RepairCell';
import type { RowRepair } from '../repairs';
import { GradeSyncLink } from '../gradeSyncPath';
import AssignmentCell from './AssignmentCell';
import ClassesCell from './ClassesCell';
import StatusCell from './StatusCell';
import Wiring from './Wiring';

const column = createColumnHelper<AssignmentRow>();

// AIDEV-NOTE: The way through to the teacher's page, so it is shown wherever the rows can be more than
// one person's, repetition included. Only the teacher's own page leaves it out — there it links to itself.
const teacherColumn = column.accessor('teacher', {
  cell: (info) => {
    const { teacher_id: teacherId } = info.row.original;

    if (teacherId == null) return info.getValue() ?? '—';

    return (
      <GradeSyncLink to={`/teachers/${teacherId}`}>
        {info.getValue() ?? teacherId}
      </GradeSyncLink>
    );
  },
  header: 'Teacher',
});

interface AssignmentsTableMeta {
  match: Map<number, MatchRow> | null;
  repairs: Map<number, RowRepair> | null;
  stafferId: string;
}

const tableMeta = (meta: unknown) => meta as AssignmentsTableMeta;

const assignmentColumn = column.accessor(
  row => [row.location, row.label, row.assignment_title, ...row.items, row.assignment_id].join(' '),
  {
    cell: info => <AssignmentCell row={info.row.original} />,
    header: 'Assignment',
    id: 'assignment',
    meta: { width: 'auto' },
  },
);

const classesColumn = column.accessor(row => row.class_names.join(' '), {
  cell: info => <ClassesCell row={info.row.original} />,
  header: 'Classes',
  id: 'classes',
});

const dueColumn = column.accessor('due_at', {
  cell: info => (info.getValue() ? new Date(info.getValue() as string).toLocaleDateString() : '—'),
  header: 'Due',
});

const statusColumn = column.accessor('status', {
  // Without repairs there is no Action column to say what is wrong, so the finding sits under the status.
  cell: ({ row, table }) => (
    <StatusCell row={row.original} showDiagnosis={tableMeta(table.options.meta).repairs == null} />
  ),
  header: 'Status',
});

const deliveredColumn = column.accessor('delivered', {
  cell: (info) => {
    const {
      status, students_with_scores: scored, twin_covered: covered, uncovered,
    } = info.row.original;
    const own = info.getValue();
    // Under a duplicate the missing students show on the copy for their class, not as a fault here.
    const missing = status === 'covered_by_twin' ? 0 : uncovered;

    // Leads with whether each student's grade is in the LMS at all, from this copy or another.
    return (
      <span>
        {`${own + covered} of ${scored}`}
        {(covered > 0 || missing > 0) && (
          <span className="tw-block tw-text-sm tw-text-spa-secondary">
            {covered > 0 && (own > 0 ? `${own} from this, ${covered} via a duplicate` : 'via a duplicate')}
            {covered > 0 && missing > 0 && ' · '}
            {missing > 0 && <span className="tw-text-spa-error">{`${missing} missing`}</span>}
          </span>
        )}
      </span>
    );
  },
  header: 'Scores in LMS',
  meta: { align: 'right' },
});

const lastDeliveryColumn = column.accessor('last_delivery', {
  cell: info => (info.getValue() ? new Date(info.getValue() as string).toLocaleDateString() : '—'),
  header: 'Last delivery',
});

// With repairs in hand the row carries its fix; the finding text it replaces only restated why.
const actionColumn = column.display({
  cell: ({ row, table }) => {
    const { repairs, stafferId } = tableMeta(table.options.meta);

    return (
      <RepairCell
        id={row.original.assignment_id}
        repair={repairs?.get(row.original.assignment_id)}
        stafferId={stafferId}
      />
    );
  },
  header: 'Action',
  id: 'action',
});

const wiringColumn = column.display({
  cell: ({ row, table }) => <Wiring row={tableMeta(table.options.meta).match?.get(row.original.assignment_id)} />,
  header: 'Wiring',
  id: 'wiring',
});

interface ColumnOptions {
  hasMatch: boolean;
  hasRepairs: boolean;
  showTeacher: boolean;
}

// AIDEV-NOTE: Every column is module-level and reads per-render data from the table's meta; only the
// list of which ones show is built per render. New cell functions each render would remount every row.
const columnsFor = ({ hasMatch, hasRepairs, showTeacher }: ColumnOptions) => [
  assignmentColumn,
  ...(showTeacher ? [teacherColumn] : []),
  classesColumn,
  dueColumn,
  statusColumn,
  deliveredColumn,
  lastDeliveryColumn,
  ...(hasRepairs ? [actionColumn] : []),
  ...(hasMatch ? [wiringColumn] : []),
] as ColumnDef<AssignmentRow, unknown>[];

interface AssignmentsTableProps {
  isLoading: boolean;
  /** Null until the LMS rosters have been read; the column only exists once they have. */
  match: Map<number, MatchRow> | null;
  /** When present, an Action column carries each row's fix; without it, the finding shows under the status. */
  repairs?: Map<number, RowRepair> | null;
  rows: AssignmentRow[];
  showTeacher?: boolean;
  stafferId?: string;
}

const AssignmentsTable = ({
  isLoading, match, repairs = null, rows, showTeacher = true, stafferId = '',
}: AssignmentsTableProps) => (
  <SPADataTable
    columns={columnsFor({ hasMatch: match != null, hasRepairs: repairs != null, showTeacher })}
    data={rows}
    defaultSort={[]}
    emptyMessage="No assignments with graded work or rejected posts match those filters."
    footnote={(
      <>
        Ordered worst first, by how many scores are missing from the LMS. Rate limited and skipped posts
        never reached the LMS: our limiter held them back, or the sync had a reason not to send (see the
        assignment page).
      </>
    )}
    globalFilter={{ placeholder: 'Filter by lesson, item, class or ID…' }}
    isLoading={isLoading}
    meta={{ match, repairs, stafferId }}
    pagination={{ defaultPageSize: 50 }}
  />
);

export default AssignmentsTable;
